import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { DigitaServiceError } from '@digitaservice/utils';
import { addBreadcrumb } from '@sentry/angular';
import { filter, take } from 'rxjs/operators';
import { ILink } from 'src/app/api/modules/core/components/abstract/ILink';
import { ISnackbar } from 'src/app/api/modules/core/components/snackbar/ISnackbar';
import { IUnauthorizedResponse } from 'src/app/api/modules/core/network/IUnauthorizedResponse';
import { AppRepository } from '../application/application.repository';
import { LinkService } from '../link.service';
import { SnackbarService } from '../snackbar.service';
import { CredentialService } from './credential.service';

/**
 * What the caller should do with a failure it handed to {@link SessionExpiryService}.
 */
export interface SessionExpiryOutcome {
  /**
   * Owned here: the caller should complete without a value and report nothing, because either the
   * redirect has been started or another request already started it.
   *
   * `false` means the failure belongs on the caller's normal error path.
   */
  readonly handled: boolean;

  /**
   * Wording the server wrote for the participant, when the response carried any. Only ever set
   * alongside `handled: false`, since a followed redirect shows nobody an error.
   *
   * Raise it in place of anything the caller would compose itself. A caller's own wording
   * describes the transport — "Http failure response for &lt;url&gt;" — which names this API to
   * someone who cannot act on it, and a session ending is an ordinary event rather than a fault
   * worth showing them a URL for.
   *
   * Declared as a plain optional rather than discriminated against `handled`, because this
   * workspace does not yet run `strictNullChecks` and narrowing a union on a boolean literal
   * silently fails without it.
   */
  readonly failure?: DigitaServiceError;
}

/**
 * Turns a `401 Unauthorized` from any endpoint into one redirect.
 *
 * A session can lapse at any point in an experience, and the request that discovers it is
 * whichever one happens to be next — a screen route, a leaderboard poll, a file upload, a game
 * reporting its result. None of those callers knows anything about sessions, and each of them
 * would otherwise report the failure in its own way: the route resolver puts the fatal error
 * screen up, a poll stops without saying why, an upload leaves its progress bar where it was.
 * Answering all of them from here is what lets the server end a session from any endpoint and get
 * the same outcome.
 *
 * What arrives is a status code and a body the server wrote — see {@link IUnauthorizedResponse}
 * for the contract. A 401 naming a destination is not a fault, so nothing is captured for it and
 * no error screen is shown; a 401 naming nothing is left to the caller's usual error path, which
 * does both. So is every 401 after a redirect has been attempted and did not arrive — one attempt
 * is made per page load, and what follows is reported rather than tried again.
 */
@Injectable({
  providedIn: 'root',
})
export class SessionExpiryService {
  private readonly router = inject(Router);
  private readonly linkService = inject(LinkService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly credentialService = inject(CredentialService);
  private readonly appRepository = inject(AppRepository);

  /**
   * How far the one redirect this page is allowed has got.
   *
   * A session ends for every request at once, not for one of them, so a screen holding a poll and
   * an upload can produce three 401s within a few milliseconds of each other. Each is a genuine
   * report of the same single event, and acting on all three would run the redirect three times —
   * three snackbars, and for an external destination three assignments to the window's location.
   * `inFlight` is what stops the second and the third.
   *
   * Back to `idle` once the destination has rendered, rather than held for the lifetime of the
   * app, so that a session which ends a second time is still acted on. An external destination
   * never completes a navigation and so never returns to it, which is the right outcome: the page
   * itself is on its way out.
   *
   * Reaching `abandoned` also stops the loading indicator, which no other exit is left to do.
   *
   * `abandoned` is terminal, and it is what keeps this out of a loop. A destination navigation
   * that never arrives — cancelled, errored, or answered 401 in turn by its own resolver — has
   * shown that the redirect does not work from here, and running it again for the next 401 would
   * ask the same question and get the same answer for as long as anything keeps making requests.
   * From `abandoned` every further 401 takes the caller's error path instead, which is visible to
   * the participant and reported to us. One attempt, then the truth.
   */
  private redirect: 'idle' | 'inFlight' | 'abandoned' = 'idle';

  /**
   * Takes ownership of a failed request when it is a session ending with somewhere to go.
   *
   * @param error - the failure caught by the API service, whatever its status.
   * @returns what the caller should do — see {@link SessionExpiryOutcome}.
   */
  handleUnauthorized(error: HttpErrorResponse): SessionExpiryOutcome {
    if (error.status !== 401) {
      return { handled: false };
    }

    if (this.redirect === 'inFlight') {
      addBreadcrumb({
        category: 'auth',
        level: 'info',
        message: 'Session expiry already being handled; this request stopped without reporting',
        data: { url: error.url ?? undefined },
      });
      return { handled: true };
    }

    const body = readUnauthorizedBody(error.error);

    // A redirect that was started and never arrived is not started again — see `redirect`.
    if (this.redirect === 'abandoned') {
      return { handled: false, failure: readServerFailure(body?.error) };
    }

    const destination = this.readUsableDestination(body?.destination);

    // No usable destination means the server has ended the session without saying where the
    // participant belongs now. That is a server-side gap rather than something to paper over
    // here, so it goes down the normal error path: the participant gets the error screen and the
    // failure is reported with its 401 attached. Which is the point of refusing rather than
    // trying: a visible error that names the endpoint beats a redirect that quietly leaves the
    // participant stranded. Any wording the server sent travels with it, because the alternative
    // the caller composes describes the transport rather than the participant's situation.
    if (!destination) {
      return { handled: false, failure: readServerFailure(body?.error) };
    }

    this.redirect = 'inFlight';
    this.settleOnArrival(destination);

    // Applied in the order a successful response applies them, and both before the navigation:
    // the credential because the destination's own request needs it, and the snackbar because the
    // overlay it opens outlives a router navigation and so is still readable on arrival.
    if (body?.credential) {
      this.credentialService.applyCredential(body.credential);
    }
    const snackbar = readSnackbar(body?.snackbar);
    if (snackbar) {
      this.snackbarService.open(snackbar);
    }

    addBreadcrumb({
      category: 'auth',
      level: 'info',
      message: 'Session expired; redirecting',
      data: {
        url: error.url ?? undefined,
        href: destination.href,
        type: destination.type,
        replacedCredential: Boolean(body?.credential),
      },
    });

    this.linkService.openLink(destination);
    return { handled: true };
  }

  /**
   * Reads a destination that will actually take the participant off the screen they are on.
   *
   * The request that raised the 401 has already been abandoned by the time this is read, so
   * whatever screen was showing is now inert: its poll has stopped, or its navigation never
   * completed, or its upload has gone. A destination that leaves that screen in place therefore
   * does not end the session for the participant, it strands them on it. Three of the link types
   * do exactly that and are refused here:
   *
   * - `external-blank` and `social` open a second window and leave this one untouched.
   * - `none` performs no navigation at all. It describes a link whose own screen reacts to the
   *   activation instead, and there is no screen involved in a session ending.
   *
   * An internal route additionally needs the app to have been configured. Every one of the
   * theme, shell, footer, dictionary and cookie settings arrives on the initialize response, so
   * a 401 answering initialize itself means none of them landed — and routing inside an app in
   * that state renders a screen into an unstyled shell with no copy, silently, which is far
   * harder to diagnose than the error screen. The contract asks for no destination at all on an
   * initialize 401, since nothing that leaves the page works from inside an embed either; this
   * refusal is what holds if one arrives anyway.
   *
   * `href` is required throughout, since it is the one property no navigation can proceed
   * without. An unrecognized `type` is deliberately treated as an internal route, matching what
   * the link handling does with one everywhere else.
   *
   * The scheme is deliberately not checked. Link handling refuses `javascript:`, `data:` and
   * `vbscript:` on any link, and it would refuse one sent here too — but it refuses by returning
   * without navigating, which leaves the guard waiting for an arrival that never comes and every
   * later 401 unheard. That is left unguarded because a server naming one of those schemes as a
   * session destination is not a case worth carrying code for. This note is here so that whoever
   * meets the silence knows where it came from.
   *
   * @param destination - the `destination` read off the 401 body.
   * @returns the destination to follow, or `undefined` when there is nothing usable.
   */
  private readUsableDestination(destination: unknown): ILink | undefined {
    if (typeof destination !== 'object' || destination === null) {
      return undefined;
    }

    const { href, type } = destination as Partial<ILink>;
    if (typeof href !== 'string' || href === '') {
      return undefined;
    }

    if (type === 'none' || type === 'external-blank' || type === 'social') {
      this.reportUnusableDestination(href, type, 'this link type leaves the current page in place');
      return undefined;
    }

    const leavesThePage = type === 'external-self' || type === 'external-parent' || type === 'external-top';
    if (!leavesThePage && !this.appRepository.getValue().hasInitialized) {
      this.reportUnusableDestination(href, type, 'an internal route needs an initialized app');
      return undefined;
    }

    return destination as ILink;
  }

  /**
   * Records a destination that was named but could not be followed.
   *
   * Worth its own trail because the participant ends up on the error screen either way, which on
   * its own looks identical to a 401 that named nothing at all — and the two need different
   * fixes on the server.
   *
   * @param href - the destination's href.
   * @param type - the destination's type, as sent.
   * @param reason - why it could not be followed.
   */
  private reportUnusableDestination(href: string, type: string | undefined, reason: string): void {
    addBreadcrumb({
      category: 'auth',
      level: 'warning',
      message: `Session expiry named a destination that cannot be followed: ${reason}`,
      data: { href, type },
    });
  }

  /**
   * Watches the destination's navigation and records how it ended.
   *
   * Subscribed before the navigation is asked for, so the destination's own event is the one that
   * settles it.
   *
   * The cancellation that matters is told apart from the one that does not by its url. Asking for
   * the destination abandons whatever navigation raised the 401, and that abandonment raises
   * `NavigationCancel` a moment later — settling on it would reopen the window this guard exists
   * to close. `NavigationCancel` and `NavigationError` therefore only count when they name the
   * destination itself.
   *
   * `NavigationEnd` is matched whatever it names, because nothing else navigates while a redirect
   * is in flight, and because a destination whose url normalizes differently from the one the
   * router reports would otherwise leave this subscribed forever — which is the state this exists
   * to prevent. Missing a release strands the participant; matching one navigation too many costs
   * at worst a second redirect for a second expiry, which is what is wanted anyway.
   *
   * @param destination - the destination just handed to the link service.
   */
  private settleOnArrival(destination: ILink): void {
    const target = this.router.serializeUrl(this.router.parseUrl(destination.href));

    this.router.events
      .pipe(
        filter((event) => {
          if (event instanceof NavigationEnd) {
            return true;
          }
          if (event instanceof NavigationCancel || event instanceof NavigationError) {
            return this.router.serializeUrl(this.router.parseUrl(event.url)) === target;
          }
          return false;
        }),
        take(1),
      )
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.redirect = 'idle';
          return;
        }

        this.redirect = 'abandoned';

        // The route resolver clears the loader on each of its two exits — a response arriving, or
        // a failure being reported — and a 401 owned here takes neither, because the caller
        // completes without a value instead. A redirect that arrives hands the loader to the
        // destination's own resolve and it clears there. One that never arrives would otherwise
        // leave it spinning for the life of the page, over a screen nobody is coming to.
        this.appRepository.applyShellLoader(false);

        addBreadcrumb({
          category: 'auth',
          level: 'warning',
          message: 'Session expiry redirect never arrived; further 401s will be reported normally',
          data: { href: destination.href, type: destination.type },
        });
      });
  }
}

/**
 * Reads a 401 body as the response contract, or nothing when it is not one.
 *
 * The body of an error response is whatever came back: a parsed object when the server answered
 * with JSON, but the raw text when it answered with anything else. A 401 raised by something in
 * front of the application — a proxy, a load balancer, a web application firewall — typically
 * arrives as an HTML page or as nothing at all, and has to read as "no destination" rather than
 * throw while the app is already failing.
 *
 * @param body - the `error` property of the `HttpErrorResponse`.
 */
function readUnauthorizedBody(body: unknown): IUnauthorizedResponse | undefined {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) {
    return undefined;
  }
  return body as IUnauthorizedResponse;
}

/**
 * Reads wording the server wrote for the participant off a 401 body.
 *
 * Raised in place of the caller's own message, which for a failed request describes the transport
 * and embeds the API URL — true, but addressed to us rather than to whoever is holding the phone,
 * and a session ending is an ordinary event rather than a fault worth showing them a URL for.
 *
 * Stamped as a `response` failure because that is what it is: the server composed this text and
 * sent it, exactly as it does on a successful response carrying an `error`. The stamp is also what
 * marks the message as server-written, so nothing downstream substitutes generic copy for it.
 *
 * @param serverError - the `error` read off the 401 body.
 * @returns the error to raise, or `undefined` when the server sent no wording.
 */
function readServerFailure(serverError: unknown): DigitaServiceError | undefined {
  if (typeof serverError !== 'object' || serverError === null) {
    return undefined;
  }

  const { message } = serverError as { message?: unknown };
  if (typeof message !== 'string' || message === '') {
    return undefined;
  }

  const failure = new DigitaServiceError(message);
  return Object.assign(failure, { endpointFailure: 'response' });
}

/**
 * Reads a snackbar worth opening.
 *
 * `message` is what a snackbar renders, so one without it would open an empty bar over the
 * destination.
 *
 * @param snackbar - the `snackbar` read off the 401 body.
 */
function readSnackbar(snackbar: unknown): ISnackbar | undefined {
  if (typeof snackbar !== 'object' || snackbar === null) {
    return undefined;
  }

  const { message } = snackbar as Partial<ISnackbar>;
  if (typeof message !== 'string' || message === '') {
    return undefined;
  }

  return snackbar as ISnackbar;
}
